import { useState, useEffect } from "react";
import axiosInstance from "../../api/axiosInstance";
import { toast } from "react-toastify";

const STATUS_OPTIONS = ["new", "contacted", "closed"];

const statusColor = {
  new: "bg-danger",
  contacted: "bg-warning text-dark",
  closed: "bg-success",
};

function EnquiriesPage() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchEnquiries();
  }, [filter]);

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const url = filter ? `/api/enquiries?status=${filter}` : "/api/enquiries";
      const res = await axiosInstance.get(url);
      setEnquiries(res.data);
    } catch (err) {
      toast.error("Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axiosInstance.put(`/api/enquiries/${id}`, { status: newStatus });
      toast.success("Status updated");
      fetchEnquiries();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update status");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete enquiry from "${name}"?`)) return;
    try {
      await axiosInstance.delete(`/api/enquiries/${id}`);
      toast.success("Enquiry deleted");
      fetchEnquiries();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete");
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <h4 className="mb-0">Enquiries</h4>
        <select
          className="form-select form-select-sm"
          style={{ width: "auto" }}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      ) : (
        <div className="card border-0 shadow-sm">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th className="d-none d-md-table-cell">Contact</th>
                  <th className="d-none d-lg-table-cell">Subject</th>
                  <th className="d-none d-md-table-cell">Date</th>
                  <th style={{ width: "130px" }}>Status</th>
                  <th style={{ width: "80px" }} className="text-end">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {enquiries.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4">
                      No enquiries found.
                    </td>
                  </tr>
                ) : (
                  enquiries.map((enq) => (
                    <>
                      <tr
                        key={enq.id}
                        role="button"
                        onClick={() =>
                          setExpandedId(expandedId === enq.id ? null : enq.id)
                        }
                      >
                        <td className="fw-medium">{enq.name}</td>
                        <td className="d-none d-md-table-cell text-muted small">
                          {enq.email || enq.phone || "—"}
                        </td>
                        <td className="d-none d-lg-table-cell text-muted small">
                          {enq.subject || "—"}
                        </td>
                        <td className="d-none d-md-table-cell text-muted small">
                          {new Date(enq.created_at).toLocaleDateString()}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <select
                            className={`form-select form-select-sm border-0 text-white ${statusColor[enq.status]}`}
                            value={enq.status}
                            onChange={(e) =>
                              handleStatusChange(enq.id, e.target.value)
                            }
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td
                          className="text-end"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(enq.id, enq.name)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                      {expandedId === enq.id && (
                        <tr>
                          <td colSpan="6" className="bg-light">
                            <div className="p-2">
                              <strong>Message:</strong>
                              <p className="mb-1">{enq.message}</p>
                              <div className="d-flex gap-3 text-muted small">
                                {enq.email && (
                                  <span>
                                    <i className="bi bi-envelope me-1"></i>
                                    {enq.email}
                                  </span>
                                )}
                                {enq.phone && (
                                  <span>
                                    <i className="bi bi-telephone me-1"></i>
                                    {enq.phone}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default EnquiriesPage;
